import { useQuery } from '@redwoodjs/web';

import { showErrorToast } from 'src/components/Toasts/toast';
import { GET_ADDRESS_COORDINATES } from 'src/graphql/queries/addressCoordinates';

interface UseAddressCoordinatesParams {
    eircode?: string;
    country?: string;
    enabled?: boolean;
}

interface AddressCoordinate {
    name: string | null;
    latitude: number | null;
    longitude: number | null;
}

interface UseAddressCoordinatesReturn {
    data: AddressCoordinate;
    loading: boolean;
    error: any;
    refetch: () => Promise<void>;
}

const npiOfficeCoordinates = {
    latitude: 53.29288290775429,
    longitude: -6.426774858980775,
    name: 'NPI Office',
};

function useAddressCoordinates({
    eircode,
    country,
    enabled = true,
}: UseAddressCoordinatesParams): UseAddressCoordinatesReturn {
    const shouldSkip = !eircode || !enabled;

    const queryResult = useQuery(GET_ADDRESS_COORDINATES, {
        skip: shouldSkip,
        variables: {
            eircode,
            country,
        },
        onError: () => {
            showErrorToast('Unable to retrieve location.');
        },
    });

    const createDefaultRefetch = (): (() => Promise<void>) => {
        return async () => {
            return Promise.resolve();
        };
    };

    const createReturnObject = (
        data: AddressCoordinate,
        loading: boolean,
        error: any,
        refetch?: (() => Promise<void>) | undefined
    ): UseAddressCoordinatesReturn => {
        return {
            data,
            loading,
            error,
            refetch: refetch || createDefaultRefetch(),
        };
    };

    if (shouldSkip) {
        return createReturnObject(npiOfficeCoordinates, false, undefined);
    }

    if (!queryResult) {
        return createReturnObject(npiOfficeCoordinates, true, undefined);
    }

    const { data, loading, error, refetch } = queryResult;

    if (!data?.addressCoordinate) {
        return createReturnObject(npiOfficeCoordinates, loading, error, refetch);
    }

    const coordinateData: AddressCoordinate = {
        name: data.addressCoordinate.name,
        latitude: data.addressCoordinate.latitude,
        longitude: data.addressCoordinate.longitude,
    };

    return createReturnObject(coordinateData, loading, error, refetch);
}

export default useAddressCoordinates;
